import { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Loader2, Upload, ZoomIn, ZoomOut, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react'
import type { DocumentData, BoundingBox } from '@/store/dataCatalogSlice'
import { toast } from 'sonner'
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?url'
import { useTranslation } from '@/i18n'
import { colors } from '@/styles/colors'
import { nextStepPercent } from '@/hooks/useZoom'
import { clampPercent, ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER } from '@/constants/zoom'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

GlobalWorkerOptions.workerSrc = pdfjsWorker

const RenderableExtensions = 'pdf, png, jpg, jpeg'

interface RegionSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSkip?: () => void
  documentData: DocumentData | null
  onSubmit: (documentDataWithBbox: DocumentData) => Promise<void>
  cropStage?: 'diagram' | 'table' | null // For PFD: 'diagram' first, then 'table'
}

const RegionSelectionModal = ({
  isOpen,
  onClose,
  documentData,
  onSubmit,
  onSkip,
  cropStage = null,
}: RegionSelectionModalProps) => {
  const { t } = useTranslation()
  const [bboxes, setBboxes] = useState<BoundingBox[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [start, setStart] = useState<{ x: number; y: number } | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [ImageUrl, setImageUrl] = useState<string | null>(null)
  const imageUrlRef = useRef<string | null>(null)
  const renderAbortRef = useRef<AbortController | null>(null)
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null)

  // Multi-page PDF state
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const pdfDocRef = useRef<Awaited<ReturnType<typeof getDocument>['promise']> | null>(null)
  const [pageBboxes, setPageBboxes] = useState<Map<number, BoundingBox[]>>(new Map())
  // Ref mirrors pageBboxes state so switchToPage always reads the latest value
  // after an async await, avoiding stale closure reads.
  const pageBboxesRef = useRef(pageBboxes)
  pageBboxesRef.current = pageBboxes
  const [skippedPages, setSkippedPages] = useState<Set<number>>(new Set())

  // Zoom and pan state
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null)
  const imageContainerRef = useRef<HTMLDivElement | null>(null)

  function isRenderable(): boolean {
    if (!documentData?.file) return false
    const extension = documentData.file.name.split('.').pop()?.toLowerCase() || ''
    return RenderableExtensions.includes(extension)
  }

  /**
   * Check if the table bbox is fully contained within at least one of the diagram bboxes.
   * Uses a small tolerance to account for drawing imprecision.
   */
  const isTableInsideAnyDiagram = (
    tableBbox: BoundingBox,
    diagramBboxes: BoundingBox[],
    tolerancePx: number = 2
  ): boolean => {
    if (diagramBboxes.length === 0) return true // No diagrams to validate against

    const tableLeft = tableBbox.x
    const tableRight = tableBbox.x + tableBbox.width
    const tableTop = tableBbox.y
    const tableBottom = tableBbox.y + tableBbox.height

    // Check if table is fully inside any one of the diagram bboxes
    for (const diagram of diagramBboxes) {
      const diagramLeft = diagram.x - tolerancePx
      const diagramRight = diagram.x + diagram.width + tolerancePx
      const diagramTop = diagram.y - tolerancePx
      const diagramBottom = diagram.y + diagram.height + tolerancePx

      if (
        tableLeft >= diagramLeft &&
        tableRight <= diagramRight &&
        tableTop >= diagramTop &&
        tableBottom <= diagramBottom
      ) {
        return true // Table is fully inside this diagram
      }
    }

    return false // Table is not fully inside any diagram
  }

  // Render a specific page from the currently loaded PDF document
  const renderPdfPage = useCallback(async (pdf: Awaited<ReturnType<typeof getDocument>['promise']>, pageNum: number, signal: AbortSignal) => {
    setLoadingPreview(true)
    try {
      const page = await pdf.getPage(pageNum)
      const viewport = page.getViewport({ scale: 2 })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const context = canvas.getContext('2d')
      await page.render({ canvasContext: context!, viewport, canvas }).promise
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
      )
      const url = URL.createObjectURL(blob)
      canvas.width = 0
      canvas.height = 0
      if (signal.aborted) {
        URL.revokeObjectURL(url)
        return
      }
      // Revoke previous image URL before setting new one
      if (imageUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current)
      }
      imageUrlRef.current = url
      setImageUrl(url)
    } catch {
      if (signal.aborted) return
      toast.error(t('data_catalog:region_selection_modal.errors.preview_failed'))
      setImageUrl(null)
    }
    if (!signal.aborted) setLoadingPreview(false)
  }, [t])

  // Load PDF document when file changes
  useEffect(() => {
    renderAbortRef.current?.abort()
    const abortController = new AbortController()
    renderAbortRef.current = abortController
    const { signal } = abortController

    setLoadingPreview(false)

    if (imageUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrlRef.current)
    }
    imageUrlRef.current = null

    // Destroy previous pdf document
    try { pdfDocRef.current?.destroy() } catch { /* ignore */ }
    pdfDocRef.current = null

    const loadPdf = async (file: File) => {
      setLoadingPreview(true)
      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdf = await getDocument({ data: arrayBuffer }).promise
        if (signal.aborted) {
          try { pdf.destroy() } catch { /* ignore */ }
          return
        }
        pdfDocRef.current = pdf
        setTotalPages(pdf.numPages)
        setCurrentPage(1)
        await renderPdfPage(pdf, 1, signal)
      } catch {
        if (signal.aborted) return
        toast.error(t('data_catalog:region_selection_modal.errors.preview_failed'))
        setImageUrl(null)
        setLoadingPreview(false)
      }
    }

    if (documentData?.file && isRenderable()) {
      if (documentData.file.type === 'application/pdf') {
        loadPdf(documentData.file)
      } else {
        const url = URL.createObjectURL(documentData.file)
        imageUrlRef.current = url
        setImageUrl(url)
        setTotalPages(1)
        setCurrentPage(1)
      }
    } else {
      setImageUrl(null)
      setTotalPages(1)
      setCurrentPage(1)
    }

    setBboxes([])
    setStart(null)
    setIsDragging(false)
    setZoom(1)
    setPan({ x: 0, y: 0 })
    setIsPanning(false)
    setPanStart(null)
    setPageBboxes(new Map())
    setSkippedPages(new Set())

    return () => {
      abortController.abort()
      if (imageUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current)
        imageUrlRef.current = null
      }
      // Only destroy pdf doc on file change or unmount — not on page switch
      if (!pdfDocRef.current) return
      try { pdfDocRef.current.destroy() } catch { /* ignore */ }
      pdfDocRef.current = null
    }
  }, [documentData?.file])

  // Preserve diagram bboxes when transitioning to table stage for PFD
  useEffect(() => {
    if (cropStage === 'table') {
      // Keep diagram bboxes, clear only non-diagram regions
      setBboxes(prev => prev.filter(b => b.type === 'diagram'))
      setStart(null)
      setIsDragging(false)
    }
  }, [cropStage])

  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null)

  // Update container size when image loads or window resizes
  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setContainerSize({ width: rect.width, height: rect.height })
      }
    }

    updateContainerSize()
    window.addEventListener('resize', updateContainerSize)
    return () => window.removeEventListener('resize', updateContainerSize)
  }, [imageSize])

  // Memoize base image dimensions to avoid recalculating on every render
  const baseImageDimensions = useMemo(() => {
    if (!imageSize || !containerSize) return null
    const scaleRatio = Math.max(imageSize.width / containerSize.width, imageSize.height / containerSize.height)
    return {
      width: imageSize.width / scaleRatio,
      height: imageSize.height / scaleRatio,
    }
  }, [imageSize, containerSize])

  const toImageCoords = (clientX: number, clientY: number) => {
    const rect = containerRef.current!.getBoundingClientRect()
    const containerX = clientX - rect.left
    const containerY = clientY - rect.top

    if (!imageSize || !imageContainerRef.current || !baseImageDimensions) {
      return { x: containerX, y: containerY }
    }

    const baseImageWidth = baseImageDimensions.width
    const baseImageHeight = baseImageDimensions.height

    // The container center
    const containerCenterX = rect.width / 2
    const containerCenterY = rect.height / 2

    // The transformed image center is offset by pan
    const transformedCenterX = containerCenterX + pan.x
    const transformedCenterY = containerCenterY + pan.y

    // Current zoomed image dimensions
    const zoomedWidth = baseImageWidth * zoom
    const zoomedHeight = baseImageHeight * zoom

    // Top-left corner of the zoomed image
    const imageTopLeftX = transformedCenterX - zoomedWidth / 2
    const imageTopLeftY = transformedCenterY - zoomedHeight / 2

    // Position relative to zoomed image top-left
    const relativeX = containerX - imageTopLeftX
    const relativeY = containerY - imageTopLeftY

    // Scale back to base image coordinates
    const imageX = relativeX / zoom
    const imageY = relativeY / zoom

    return {
      x: imageX,
      y: imageY,
    }
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!containerRef.current) return

    // Hold Shift or use middle mouse button to pan
    if (e.button === 1 || e.shiftKey) {
      e.preventDefault()
      setIsPanning(true)
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
      return
    }

    // Otherwise, draw bounding box
    const p = toImageCoords(e.clientX, e.clientY)
    setStart(p)
    setIsDragging(true)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    // Handle panning
    if (isPanning && panStart) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      })
      return
    }

    // Handle bounding box drawing
    if (!isDragging || !start) return
    const p = toImageCoords(e.clientX, e.clientY)
    const x = Math.min(start.x, p.x)
    const y = Math.min(start.y, p.y)
    const width = Math.abs(p.x - start.x)
    const height = Math.abs(p.y - start.y)

    setBboxes(prev => {
      const existingBoxes = prev.filter(b => b.id !== 'preview')
      const isFirstRegion = existingBoxes.length === 0

      // Determine preview type based on crop stage or default behavior
      let previewType: BoundingBox['type'] = 'text'
      if (cropStage === 'diagram') {
        // First region is 'diagram', rest are 'text' (user can change to table via dropdown)
        previewType = isFirstRegion ? 'diagram' : 'text'
      } else if (cropStage === 'table') {
        previewType = 'table'
      } else {
        // Default behavior for P&ID: first is 'diagram', rest are 'equipment'
        previewType = isFirstRegion ? 'diagram' : 'equipment'
      }

      const previewBox: BoundingBox = {
        id: 'preview',
        x,
        y,
        width,
        height,
        type: previewType,
        name: 'Preview',
      }

      return [...existingBoxes, previewBox]
    })
  }

  const handlePointerUp = () => {
    // Handle pan end
    if (isPanning) {
      setIsPanning(false)
      setPanStart(null)
      return
    }

    // Handle bbox drawing end
    if (!isDragging) return
    setIsDragging(false)

    setBboxes(prev => {
      const preview = prev.find(b => b.id === 'preview')
      if (!preview) return prev
      const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2, 9)

      const existingBoxes = prev.filter(b => b.id !== 'preview')
      const isFirstRegion = existingBoxes.length === 0

      // Determine type and name based on crop stage
      let boxType: BoundingBox['type'] = 'text'
      let newName = `Region ${existingBoxes.length + 1}`

      if (cropStage === 'diagram') {
        // First region is 'diagram', rest are 'text' (user can change to table via dropdown)
        boxType = isFirstRegion ? 'diagram' : 'text'
        newName = isFirstRegion ? 'Diagram Region' : newName
      } else if (cropStage === 'table') {
        boxType = 'table'
        newName = 'Table Region'
      } else {
        // Default behavior for P&ID: first is 'diagram', rest are 'equipment'
        boxType = isFirstRegion ? 'diagram' : 'equipment'
        if (!isFirstRegion) {
          const equipmentCount = existingBoxes.filter(b => b.type === 'equipment').length
          newName = `Equipment ${equipmentCount + 1}`
        } else {
          newName = 'Diagram Region'
        }
      }

      // Validate table region is inside the union of all diagram regions
      if (boxType === 'table') {
        const diagramBoxes = existingBoxes.filter(b => b.type === 'diagram')
        if (diagramBoxes.length > 0 && !isTableInsideAnyDiagram(preview, diagramBoxes)) {
          toast.error(t('data_catalog:region_selection_modal.errors.table_outside_diagram'))
          return existingBoxes // Don't add the invalid table region
        }
      }

      const newBox: BoundingBox = {
        ...preview,
        id,
        type: boxType,
        name: newName,
      }
      return [...existingBoxes, newBox]
    })

    setStart(null)
  }

  const handleDeleteBox = (id: string) => setBboxes(prev => prev.filter(b => b.id !== id))

  // Zoom handlers - zoom towards center of viewport. FS-562: grid-snapped 10%
  // steps (was ×1.2 / ÷1.2, which drifted off the 10% grid) clamped to the
  // shared raster policy.
  const stepZoom = (direction: 1 | -1) => {
    const newZoom = clampPercent(nextStepPercent(Math.round(zoom * 100), direction), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER) / 100
    if (newZoom === zoom) return
    const zoomRatio = newZoom / zoom
    setPan(prev => ({
      x: prev.x * zoomRatio,
      y: prev.y * zoomRatio,
    }))
    setZoom(newZoom)
  }

  const handleZoomIn = () => stepZoom(1)
  const handleZoomOut = () => stepZoom(-1)

  const handleResetZoom = () => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }

  // Multi-page navigation: save current page bboxes and switch to target page
  const switchToPage = useCallback(async (targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages || targetPage === currentPage) return
    if (!pdfDocRef.current) return

    // Save current page bboxes (exclude preview)
    const currentBboxes = bboxes.filter(b => b.id !== 'preview')
    setPageBboxes(prev => {
      const next = new Map(prev)
      next.set(currentPage, currentBboxes)
      return next
    })

    // Render target page
    renderAbortRef.current?.abort()
    const abortController = new AbortController()
    renderAbortRef.current = abortController
    await renderPdfPage(pdfDocRef.current, targetPage, abortController.signal)

    // Guard: if render was aborted (e.g. file changed), don't update page state
    if (abortController.signal.aborted) return

    // Restore target page bboxes — read from ref to get the latest state
    // after the async await (closure's pageBboxes may be stale).
    setCurrentPage(targetPage)
    setBboxes(pageBboxesRef.current.get(targetPage) || [])
    setStart(null)
    setIsDragging(false)
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [totalPages, currentPage, bboxes, renderPdfPage])

  // Skip current page — mark it as "don't process" and advance
  const handleSkipPage = useCallback(async () => {
    setSkippedPages(prev => new Set(prev).add(currentPage))
    if (currentPage < totalPages) {
      await switchToPage(currentPage + 1)
    }
    // Delete bboxes AFTER switchToPage completes — switchToPage saves the
    // current page's bboxes before navigating, so deleting before would be
    // overwritten by that save. Deleting after ensures the skip takes effect.
    setPageBboxes(prev => {
      const next = new Map(prev)
      next.delete(currentPage)
      return next
    })
  }, [currentPage, totalPages, switchToPage])

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()

    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return

    // Get mouse position relative to container
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    // Container center
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    // Zoom factor. Smooth (multiplicative) for the wheel, but clamped to the
    // same shared raster bounds as the +/- buttons so all three zoom paths agree
    // (FS-562 review: was [0.5, 5], inconsistent with stepZoom's [0.2, 4]).
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newZoom = Math.max(ZOOM_MIN_PERCENT / 100, Math.min(ZOOM_MAX_PERCENT_RASTER / 100, zoom * delta))

    // Adjust pan to zoom towards mouse cursor
    const zoomRatio = newZoom / zoom
    setPan(prev => ({
      x: (mouseX - centerX) * (1 - zoomRatio) + prev.x * zoomRatio,
      y: (mouseY - centerY) * (1 - zoomRatio) + prev.y * zoomRatio,
    }))

    setZoom(newZoom)
  }

  const updateBoxName = (id: string, name: string) => {
    setBboxes(prev => prev.map(b => (b.id === id ? { ...b, name } : b)))
  }

  const updateBoxType = (id: string, type: BoundingBox['type']) => {
    // Auto-rename when switching to a default type unless the user has typed a custom name.
    const defaultNameFor = (t: BoundingBox['type']): string =>
      t === 'diagram' ? 'Diagram Region'
      : t === 'table' ? 'Table Region'
      : t === 'equipment' ? 'Equipment'
      : 'Text Region'

    const current = bboxes.find(b => b.id === id)
    if (!current) return

    // Validate table region is inside the union of all other diagram regions.
    if (type === 'table') {
      const diagramBoxes = bboxes.filter(b => b.type === 'diagram' && b.id !== id)
      if (diagramBoxes.length > 0 && !isTableInsideAnyDiagram(current, diagramBoxes)) {
        toast.error(t('data_catalog:region_selection_modal.errors.table_outside_diagram'))
        return
      }
    }

    const wasDefaultName = current.name === defaultNameFor(current.type)
    const nextName = wasDefaultName ? defaultNameFor(type) : current.name
    setBboxes(prev => prev.map(b => (b.id === id ? { ...b, type, name: nextName } : b)))
  }

  const saveBox = (id: string) => {
    const box = bboxes.find(b => b.id === id)
    if (!box) return
    if (!box.name.trim()) {
      toast.error(t('data_catalog:region_selection_modal.errors.region_name_empty'))
      return
    }
    if (box.type === 'table') {
      const diagramBoxes = bboxes.filter(b => b.type === 'diagram' && b.id !== id)
      if (diagramBoxes.length > 0 && !isTableInsideAnyDiagram(box, diagramBoxes)) {
        toast.error(t('data_catalog:region_selection_modal.errors.table_outside_diagram'))
        return
      }
    }
    toast.success(t('data_catalog:region_selection_modal.region_bar.saved_toast'))
  }

  const isLastPage = currentPage >= totalPages

  const handleConfirm = async () => {
    if (!documentData?.file) {
      toast.error(t('data_catalog:region_selection_modal.errors.no_file'))
      return
    }

    // Multi-page: advance to next page instead of submitting
    if (totalPages > 1 && !isLastPage) {
      await switchToPage(currentPage + 1)
      return
    }

    if (!imageSize || !baseImageDimensions) {
      await onSubmit({ ...documentData, bboxes: null })
      return
    }

    // In multi-page mode, collect bboxes from all pages (excluding skipped)
    let allBoxes: BoundingBox[]
    const activePageCount = totalPages > 1 ? totalPages - skippedPages.size : totalPages
    if (totalPages > 1) {
      // Save current page's bboxes first (unless skipped)
      const currentBboxes = bboxes.filter(b => b.id !== 'preview')
      const finalPageBboxes = new Map(pageBboxes)
      if (!skippedPages.has(currentPage)) {
        finalPageBboxes.set(currentPage, currentBboxes)
      }

      // Flatten all pages' bboxes with page annotations, excluding skipped pages
      allBoxes = []
      for (const [pageNum, boxes] of finalPageBboxes) {
        if (skippedPages.has(pageNum)) continue
        for (const box of boxes) {
          allBoxes.push({ ...box, page: pageNum })
        }
      }
    } else {
      allBoxes = bboxes.filter(b => b.id !== 'preview')
    }

    // For PFD diagram stage, require at least one region (must check BEFORE empty fallthrough)
    if (cropStage === 'diagram' && allBoxes.length === 0) {
      toast.error(t('data_catalog:region_selection_modal.errors.select_at_least_one'))
      return
    }

    // For PFD table stage, table bbox is optional (user can skip)
    if (allBoxes.length === 0) {
      if (cropStage === 'table') {
        await onSubmit({ ...documentData, bboxes: [] })
        return
      }
      // Multi-page PDF with no regions on any non-skipped page: still pass totalPdfPages
      // so the backend creates per-page jobs (each processes the whole page).
      // >= 1 ensures a single remaining active page (e.g. page 3 of 4) still sends
      // its page number so the backend processes the correct page, not page 1.
      if (activePageCount >= 1 && totalPages > 1) {
        const activePages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => !skippedPages.has(p))
        await onSubmit({ ...documentData, bboxes: null, totalPdfPages: totalPages, pdfPageNumbers: activePages })
        return
      }
      await onSubmit({ ...documentData, bboxes: null })
      return
    }

    // For PFD table stage, validate table bbox if provided
    if (cropStage === 'table') {
      const hasTable = allBoxes.some(b => b.type === 'table')
      if (!hasTable) {
        await onSubmit({ ...documentData, bboxes: [] })
        return
      }
    }

    // Final validation: all table bboxes must be inside the union of all diagram bboxes.
    // In multi-page mode, validate per-page (a table on page 2 shouldn't be checked against page 1 diagrams).
    if (totalPages > 1) {
      const pageNums = new Set(allBoxes.map(b => b.page).filter((p): p is number => p != null))
      for (const pageNum of pageNums) {
        const pageBoxes = allBoxes.filter(b => b.page === pageNum)
        const pageDiagrams = pageBoxes.filter(b => b.type === 'diagram')
        const pageTables = pageBoxes.filter(b => b.type === 'table')
        if (pageDiagrams.length > 0 && pageTables.length > 0) {
          for (const tableBox of pageTables) {
            if (!isTableInsideAnyDiagram(tableBox, pageDiagrams)) {
              toast.error(t('data_catalog:region_selection_modal.errors.all_tables_outside_diagram'))
              return
            }
          }
        }
      }
    } else {
      const diagramBoxes = allBoxes.filter(b => b.type === 'diagram')
      const tableBoxes = allBoxes.filter(b => b.type === 'table')
      if (diagramBoxes.length > 0 && tableBoxes.length > 0) {
        for (const tableBox of tableBoxes) {
          if (!isTableInsideAnyDiagram(tableBox, diagramBoxes)) {
            toast.error(t('data_catalog:region_selection_modal.errors.all_tables_outside_diagram'))
            return
          }
        }
      }
    }

    // NOTE: normalization uses the CURRENT page's baseImageDimensions for all pages.
    // This is correct for P&ID PDFs which have uniform page sizes. If pages ever had
    // different sizes, we'd need to store baseImageDimensions per page.
    const baseImageWidth = baseImageDimensions.width
    const baseImageHeight = baseImageDimensions.height

    // Convert from base image coordinates to normalized ratios (0-1)
    const ratioBoxes = allBoxes.map(b => {
      const x = Math.max(0, Math.min(b.x / baseImageWidth, 1))
      const y = Math.max(0, Math.min(b.y / baseImageHeight, 1))
      const ratioWidth = Math.max(0, Math.min(b.width / baseImageWidth, 1 - x))
      const ratioHeight = Math.max(0, Math.min(b.height / baseImageHeight, 1 - y))
      return { ...b, x, y, width: ratioWidth, height: ratioHeight }
    })

    const activePages = totalPages > 1
      ? Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => !skippedPages.has(p))
      : undefined
    await onSubmit({
      ...documentData,
      bboxes: ratioBoxes,
      ...(activePageCount >= 1 && totalPages > 1 && { totalPdfPages: totalPages, pdfPageNumbers: activePages }),
    })
  }

  if (!isOpen) return null

  return (
      <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        cropStage === 'diagram'
          ? t('data_catalog:region_selection_modal.step_diagram', { defaultValue: 'Step 1: Select Diagram Region' })
          : cropStage === 'table'
          ? t('data_catalog:region_selection_modal.step_table', { defaultValue: 'Step 2: Select Table Region' })
          : totalPages > 1
          ? `${t('data_catalog:region_selection_modal.title')} — ${t('data_catalog:region_selection_modal.page_indicator', { current: currentPage, total: totalPages, defaultValue: `Page ${currentPage} of ${totalPages}` })}`
          : t('data_catalog:region_selection_modal.title')
      }
      className="max-w-[90vw] w-[90vw]"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('data_catalog:region_selection_modal.cancel_button')}
          </Button>
          {onSkip && (
            <Button variant="outline" onClick={onSkip} disabled={loadingPreview}>
              {t('data_catalog:region_selection_modal.skip_button')}
            </Button>
          )}
          {totalPages > 1 && (
            <Button
              variant="outline"
              onClick={handleSkipPage}
              disabled={loadingPreview || skippedPages.has(currentPage)}
            >
              {skippedPages.has(currentPage)
                ? t('data_catalog:region_selection_modal.skipped', 'Skipped')
                : t('data_catalog:region_selection_modal.skip_page', 'Skip Page')}
            </Button>
          )}
          <Button onClick={handleConfirm} disabled={!documentData?.file || loadingPreview || (totalPages > 1 && skippedPages.size >= totalPages)}>
            {loadingPreview ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : totalPages > 1 && !isLastPage ? (
              <ChevronRight className="w-5 h-5 mr-2" />
            ) : (
              <Upload className="w-5 h-5 mr-2" />
            )}
            {totalPages > 1 && !isLastPage
              ? t('data_catalog:region_selection_modal.next_page_button', { current: currentPage, total: totalPages, defaultValue: `Next Page (${currentPage}/${totalPages})` })
              : t('data_catalog:region_selection_modal.submit_button')}
          </Button>
        </>
      }
    >
      <div className="flex gap-4 h-[80vh]">
        <div className="relative flex-1 max-h-[80vh] border rounded overflow-hidden bg-gray-50 dark:bg-dark-muted">
          {!documentData?.file ? (
            <div className="flex items-center justify-center w-full h-full text-gray-400 dark:text-gray-500">
              {t('data_catalog:region_selection_modal.no_file_placeholder')}
            </div>
          ) : isRenderable() ? (
            <div
              ref={containerRef}
              className="relative w-full h-full"
              style={{ cursor: isPanning ? 'grabbing' : 'crosshair' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onWheel={handleWheel}
            >
              {/* Zoom Controls */}
              <div className="absolute bottom-4 right-4 z-20 flex flex-row gap-2 bg-white dark:bg-dark-card rounded-lg shadow-lg p-2 items-center">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleZoomOut}
                  disabled={zoom <= ZOOM_MIN_PERCENT / 100}
                  title={t('data_catalog:region_selection_modal.zoom_out', { defaultValue: 'Zoom Out' })}
                  className="w-8 h-8"
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <div className="text-xs text-center font-medium px-2 min-w-[40px] text-gray-600 dark:text-gray-400">
                  {Math.round(zoom * 100)}%
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleZoomIn}
                  disabled={zoom >= ZOOM_MAX_PERCENT_RASTER / 100}
                  title={t('data_catalog:region_selection_modal.zoom_in', { defaultValue: 'Zoom In' })}
                  className="w-8 h-8"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleResetZoom}
                  disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                  title={t('data_catalog:region_selection_modal.reset_view', { defaultValue: 'Reset View' })}
                  className="w-8 h-8"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Page Navigation — shown for multi-page PDFs */}
              {totalPages > 1 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white dark:bg-dark-card rounded-lg shadow-lg px-3 py-1.5">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7"
                    disabled={currentPage <= 1 || loadingPreview}
                    onClick={() => switchToPage(currentPage - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      defaultValue={currentPage}
                      key={currentPage}
                      disabled={loadingPreview}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value, 10)
                        if (val >= 1 && val <= totalPages && val !== currentPage) switchToPage(val)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = parseInt((e.target as HTMLInputElement).value, 10)
                          if (val >= 1 && val <= totalPages && val !== currentPage) switchToPage(val)
                        }
                      }}
                      className="w-10 h-6 text-center text-xs rounded border border-gray-300 dark:border-gray-600 bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span>/ {totalPages}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-7 h-7"
                    disabled={currentPage >= totalPages || loadingPreview}
                    onClick={() => switchToPage(currentPage + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {skippedPages.has(currentPage) && (
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-gray-100/80 dark:bg-gray-900/80">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-500 dark:text-gray-400">{t('data_catalog:region_selection_modal.page_skipped', 'Page Skipped')}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t('data_catalog:region_selection_modal.page_skipped_description', "This page won't be processed")}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setSkippedPages(prev => {
                        const next = new Set(prev)
                        next.delete(currentPage)
                        return next
                      })}
                    >
                      {t('data_catalog:region_selection_modal.unskip', 'Unskip')}
                    </Button>
                  </div>
                </div>
              )}

              {loadingPreview && (
                <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: colors.info[500] }}/>
                </div>
              )}

              {ImageUrl && (
                <div
                  ref={imageContainerRef}
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div
                    className="relative"
                    style={{
                      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                      transformOrigin: 'center',
                      width: baseImageDimensions?.width ?? 'auto',
                      height: baseImageDimensions?.height ?? 'auto',
                    }}
                  >
                    <img
                      src={ImageUrl}
                      alt={t('data_catalog:region_selection_modal.preview_alt', { defaultValue: 'preview' })}
                      className="select-none"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                      onLoad={e => {
                        const img = e.currentTarget
                        setImageSize({ width: img.naturalWidth, height: img.naturalHeight })
                      }}
                    />

                    {/* Bounding boxes - rendered inside transformed container */}
                    {bboxes.map(box => (
                      <div
                        key={box.id}
                        style={{
                          position: 'absolute',
                          left: box.x,
                          top: box.y,
                          width: box.width,
                          height: box.height,
                          border:
                            box.id === 'preview'
                              ? `2px dashed ${colors.blue[700]}E6`
                              : box.type === 'diagram'
                              ? `2px solid ${colors.green[600]}E6`
                              : box.type === 'table'
                              ? `2px solid ${colors.orange[600]}E6`
                              : box.type === 'equipment'
                              ? `2px solid ${colors.violet[600]}E6`
                              : `2px solid ${colors.blue[700]}E6`,
                          background:
                            box.id === 'preview'
                              ? `${colors.blue[700]}2E`
                              : box.type === 'diagram'
                              ? `${colors.green[600]}2E`
                              : box.type === 'table'
                              ? `${colors.orange[600]}2E`
                              : box.type === 'equipment'
                              ? `${colors.violet[600]}2E`
                              : `${colors.blue[700]}2E`,
                          borderRadius: 4,
                          pointerEvents: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: box.type === 'diagram' ? colors.green[600] : box.type === 'table' ? colors.orange[600] : box.type === 'equipment' ? colors.violet[600] : colors.blue[700],
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          textShadow: '0 0 2px white',
                        }}
                      >
                        {box.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-full text-gray-400 dark:text-gray-500">
              {t('data_catalog:region_selection_modal.preview_failure')}
            </div>
          )}
        </div>

        <div className="w-[320px] flex flex-col border rounded-xl p-4 shadow-sm overflow-y-auto bg-white/70 dark:bg-dark-card/70">
          {cropStage === 'table' && (
            <div className="mb-3 p-3 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                {t('data_catalog:region_selection_modal.diagram_region_selected', { defaultValue: '✓ Diagram region selected' })}
              </p>
              <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                {t('data_catalog:region_selection_modal.now_select_table', { defaultValue: 'Now select the table region below' })}
              </p>
            </div>
          )}
          <h3 className="font-semibold mb-3 text-base text-gray-700 dark:text-gray-300">
            {cropStage === 'table'
              ? t('data_catalog:region_selection_modal.select_table_region', { defaultValue: 'Select Table Region' })
              : t('data_catalog:region_selection_modal.region_bar.selected_regions')}
          </h3>

          {bboxes.filter(b => b.id !== 'preview').length === 0 ? (
            <p className="text-sm italic text-center py-4 text-gray-400 dark:text-gray-500">
              {t('data_catalog:region_selection_modal.region_bar.no_selected_region_placeholder')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {bboxes.filter(b => b.id !== 'preview').map(box => (
                <div
                  key={box.id}
                  className="flex flex-col gap-3 p-3 rounded-md border border-input bg-muted dark:bg-dark-muted"
                >
                  <div className="flex flex-col gap-2 w-full">
                    <label className="text-sm font-medium leading-5 text-foreground">
                      {t('data_catalog:region_selection_modal.region_bar.name_label')}
                    </label>
                    <Input
                      value={box.name}
                      onChange={e => updateBoxName(box.id, e.target.value)}
                      placeholder={t('data_catalog:region_selection_modal.region_bar.region_name_placeholder')}
                      className="h-9 rounded-lg bg-background"
                    />
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label className="text-sm font-medium leading-5 text-foreground">
                      {t('data_catalog:region_selection_modal.region_bar.category_label')}
                    </label>
                    <Select
                      value={box.type}
                      onValueChange={(val: string) => updateBoxType(box.id, val as BoundingBox['type'])}
                    >
                      <SelectTrigger className="h-9 rounded-lg bg-background">
                        <SelectValue placeholder={t('data_catalog:region_selection_modal.select_type')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">
                          {t('data_catalog:region_selection_modal.region_bar.region_types.text')}
                        </SelectItem>
                        <SelectItem value="diagram">
                          {t('data_catalog:region_selection_modal.region_bar.region_types.diagram')}
                        </SelectItem>
                        <SelectItem value="table">Table</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 w-full pt-1">
                    <button
                      type="button"
                      onClick={() => handleDeleteBox(box.id)}
                      className="flex-1 h-8 rounded-md text-sm font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                    >
                      {t('data_catalog:region_selection_modal.region_bar.buttons.delete')}
                    </button>
                    <button
                      type="button"
                      onClick={() => saveBox(box.id)}
                      className="flex-1 h-8 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      {t('data_catalog:region_selection_modal.region_bar.buttons.save')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default memo(RegionSelectionModal)
